import {Box, Typography} from '@mui/material';
import {t} from 'i18next';

import AurBreadcrumb from '../../../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../../../common/aur-button/AurButton';
// import { ReactComponent as ViewDoc } from '../../../../../src/assests/svg/viewDoc.svg';
import {ReactComponent as FolderNoResult} from '../../../../../src/assests/svg/folderNoResult.svg';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
} from '../../../../utils/utils';

import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../eca-doc-list/eca-save-search/EcaSaveSearch';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {useNavigate} from 'react-router-dom';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
interface TagProps {
  selectedTag?: TreeViewBaseItem<ExtendedTreeItemProps> | null;
}

const TagRightPanel: React.FC<TagProps> = ({selectedTag}) => {
  const {selectedProjectName} = useSelector(
    (state: RootState) => state.reports
  );
  const isFolder: boolean = selectedTag?.fileType ? true : false;
  const folder = selectedTag?.fileType
    ? selectedTag.label
    : selectedTag?.parentFolder;

  const breadCrumbItems = [
    {
      label: selectedProjectName ? selectedProjectName : '',
    },
    {
      label: folder ? folder : '',
    },
  ];
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();
  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const panelHeader = () => {
    return (
      <div className="panel-breadcrumb">
        <AurBreadcrumb items={breadCrumbItems} />
      </div>
    );
  };

  if (!selectedTag?.id) {
    return (
      <Box className="right-panel">
        {panelHeader()}
        <Box className="no-search-wrapper">
          <Box className="no-search-content">
            <FolderNoResult />
            <Typography className="typo-text headline2_size color_midnight_11000">
              {t('ecaHomepageModals.tabsRightPanel.tagSearch.noResultTitle')}
            </Typography>
            <Typography className="typo-text body3_size">
              {t('ecaHomepageModals.tabsRightPanel.tagSearch.noResultSubTitle')}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const renderDateTimeWithTooltip = (
    value: string,
    style?: object,
    className = 'headline3_size color_midnight_11000'
  ) => {
    return (
      <AurLightTooltip
        title={t('common.yearMonthDate')}
        placement="bottom-start"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -6], // move tooltip closer
                },
              },
            ],
          },
        }}
      >
        <Typography className={`typo-text ${className}`} style={style}>
          {getUTCDateTimeForDisplay(value)}
        </Typography>
      </AurLightTooltip>
    );
  };

  return (
    <Box className="right-panel">
      {panelHeader()}

      <Box
        className="right-panel-title"
        display="flex"
        justifyContent="space-between"
      >
        <Typography className="typo-text headline2_size color_mist_100 custom-text-wrap">
          {selectedTag.label}
        </Typography>
      </Box>

      <Box className="right-panel-details">
        <Box>
          <Typography className="typo-text subhead4_size color_midnight_11000">
            {isFolder
              ? t('ecaHomepageModals.tabsRightPanel.tagSearch.tagsInGroup')
              : t('ecaHomepageModals.tabsRightPanel.tagSearch.totalDocTag')}
          </Typography>
          <Box
            display="flex"
            // justifyContent={isFolder ? 'space-between' : 'end'}
            justifyContent={'space-between'}
            className="count-details"
          >
            <Typography className="typo-text display1_size color_midnight_11000">
              {isFolder
                ? selectedTag?.children?.length
                : formatNumberWithComma(selectedTag.count)}
            </Typography>
            {hasSearchesPerm && (
              <Box display="flex" gap={2} alignItems="center">
                <AurButton
                  ariaLabel={t(
                    'ecaHomepageModals.tabsRightPanel.tagSearch.searchTagsFolder'
                  )}
                  classNames="typo-text buttonMedium_size"
                  onClick={() => {
                    if (selectedTag) {
                      navigate('/index-search', {
                        state: {
                          source: 'tag',
                          selected: selectedTag,
                        },
                      });
                      trackGAEvent({
                        eventName: GA_EVENT_NAMES.SEARCH,
                        click: {
                          name: !isFolder
                            ? t('gaTracking.ecaHomePage.searchThisTag')
                            : t('gaTracking.ecaHomePage.searchTheseTags'),
                          region: GA_REGIONS.WIDGET_BODY,
                          type: GA_CLICK_TYPES.BUTTON,
                          action: t(
                            'gaTracking.ecaHomePage.searchForMatchingTags'
                          ),
                        },
                        subPage: {
                          subpage_name: t('gaTracking.ecaHomePage.tags'),
                          subpage_type: GA_SUBPAGE_TYPE.TAB,
                        },
                      });
                    }
                  }}
                  type="continue"
                  size="Small"
                  startIcon={
                    <AurIcon
                      icon={IconNames.Search}
                      color={IconColors.Custom}
                    />
                  }
                  dataTestId="view-docs"
                >
                  {!isFolder
                    ? t('ecaHomepageModals.tabsRightPanel.tagSearch.searchTags')
                    : t(
                        'ecaHomepageModals.tabsRightPanel.tagSearch.searchTagsFolder'
                      )}
                </AurButton>
              </Box>
            )}
          </Box>
        </Box>
        {isFolder && (
          <Box className="meta-details" display="flex">
            <Box flex={1}>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.createdBy')}
              </Typography>
              <Typography
                className="typo-text headline3_size color_midnight_11000"
                style={{marginTop: '10px'}}
              >
                {selectedTag.createdBy}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.createdOn')}
              </Typography>
              {selectedTag.createdDate &&
                renderDateTimeWithTooltip(selectedTag.createdDate, {
                  marginTop: '10px',
                })}
            </Box>
            <Box flex={1}>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.modified')}
              </Typography>
              {selectedTag.updatedDate &&
                renderDateTimeWithTooltip(selectedTag.updatedDate, {
                  marginTop: '10px',
                })}
            </Box>
          </Box>
        )}
        {!isFolder && (
          <Box className="meta-details" display="flex">
            <Box flex={1}>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.createdBy')}
              </Typography>
              <Typography
                className="typo-text headline3_size color_midnight_11000"
                style={{marginTop: '10px'}}
              >
                {selectedTag.createdBy}
              </Typography>
              {selectedTag.createdDate &&
                renderDateTimeWithTooltip(
                  selectedTag.createdDate,
                  {},
                  'body4_size color_midnight_600'
                )}
            </Box>
            <Box flex={1} hidden>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.createdOn')}
              </Typography>
              <Typography
                className="typo-text headline3_size color_midnight_11000"
                style={{marginTop: '10px'}}
              >
                {selectedTag.updatedBy}
              </Typography>
              {selectedTag.updatedDate &&
                renderDateTimeWithTooltip(
                  selectedTag.updatedDate,
                  {},
                  'body4_size color_midnight_600'
                )}
            </Box>
            <Box flex={1}>
              <Typography className="typo-text label2_size color_midnight_600">
                {t('ecaHomepageModals.tabsRightPanel.savedSearch.modified')}
              </Typography>
              {selectedTag.updatedDate &&
                renderDateTimeWithTooltip(selectedTag.updatedDate, {
                  marginTop: '10px',
                })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TagRightPanel;
